import { SavedQueryAppModel } from '@api';
import { ConfirmDialogProps, ConfirmDialog } from '@components';
import { useCanDeleteSavedQuery, useDeleteSavedQuery } from '@Inventory/services/savedQueryQueries';
import { RouterLink } from '@libs';
import { routes } from '@routes';
import { useCallback } from 'react';
import { useSearchParams, generatePath } from 'react-router-dom';
import { SAVED_QUERY_QUERY_PARAM } from './types';
import { Link } from '@mui/material';

export function SavedQueryDeleteDialog({
  savedQuery,
  open,
  onClose,
}: ConfirmDialogProps & {
  savedQuery: SavedQueryAppModel;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canDeleteSavedQuery, isCanDeleteSavedQueryLoading } = useCanDeleteSavedQuery(savedQuery.id);
  const { deleteSavedQuery, isDeleteSavedQueryLoading } = useDeleteSavedQuery(savedQuery.id);

  const handleOnConfirm = useCallback(() => {
    deleteSavedQuery();
    onClose();

    const savedQueryId = searchParams.get(SAVED_QUERY_QUERY_PARAM);
    if (savedQueryId === savedQuery.id) {
      searchParams.delete(SAVED_QUERY_QUERY_PARAM);
      setSearchParams(searchParams);
    }
  }, [deleteSavedQuery, onClose, setSearchParams, searchParams, savedQuery.id]);

  if (isCanDeleteSavedQueryLoading || isDeleteSavedQueryLoading) {
    return <ConfirmDialog open={open} onClose={onClose} loading></ConfirmDialog>;
  }

  if (!canDeleteSavedQuery?.can_delete) {
    return (
      <ConfirmDialog open={open} onClose={onClose} disproveLabel="Ok">
        <div>
          This bundle is used by those access flows:
          <ul>
            {(canDeleteSavedQuery?.access_flows_used || []).map((flow) => (
              <li key={flow.id}>
                <Link
                  component={RouterLink}
                  to={generatePath(routes.EditAccessFlow.path, { id: flow.id })}
                  underline="hover"
                >
                  {flow.name}
                </Link>
              </li>
            ))}
          </ul>
          Please, remove it from the flows before deleting.
        </div>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog open={open} onClose={onClose} onConfirm={handleOnConfirm}>
      Are you sure you want to delete <strong>{savedQuery.name}</strong> access scope?
    </ConfirmDialog>
  );
}
